// src/clock/jobFence.js
//
// Per-job geofences ("job fences") for automatic check-in / check-out.
//
// Pure logic only: no React, no Firestore. Everything here can be unit tested
// in plain Node, and the same rules will carry over unchanged when the app
// becomes a native iPhone / Android app (only the GPS source changes).
//
// Stored on the job document (jobs/{id}), additive, nothing else is changed:
//
//   jobFence: {
//     latitude, longitude     the confirmed spot on the property
//     radiusMeters            size of the fence
//     address                 the address that was used to find the spot
//     addressSource           "job"     = the job's own address
//                             "custom"  = an address typed in just for this job
//                             "device"  = the confirmer's phone location
//     pinAdjusted             true when the pin was dragged / clicked into place
//     autoCheckIn             false turns auto check-in/out off for this job
//     confirmed               true once someone has looked at it and saved
//     confirmedAt             ISO string
//     confirmedBy             name of the person who confirmed it
//     confirmedByUid
//     carriedFromJobId        set when a Continue copied the fence forward
//   }
//
// A job with no jobFence, or with confirmed !== true, behaves exactly as it
// always has: manual Check In / Check Out only.

export const DEFAULT_FENCE_RADIUS_M = 75; // about 250 ft

// Choices offered in the Confirm Job Location popup.
export const FENCE_RADIUS_OPTIONS = [50, 75, 100, 150, 200, 300];

// Phone GPS indoors is often 15–50 m off. We give the worker up to this much
// of the reported accuracy as benefit of the doubt, never more.
export const MAX_ACCURACY_BUFFER_M = 50;

// A reading worse than this is too vague to act on automatically.
export const MAX_USABLE_ACCURACY_M = 100;

// Extra distance beyond the fence before someone counts as "left". Stops a
// worker standing near the edge from flickering in and out.
export const EXIT_MARGIN_M = 25;

// Readings in a row inside a fence before an automatic check-in.
export const ENTER_CONFIRM_READINGS = 2;

// How long someone must stay outside before an automatic check-out. Long
// enough to walk to the truck and back.
export const EXIT_DWELL_MS = 3 * 60 * 1000;

// If we stop hearing from the phone for longer than this (app closed, screen
// locked) and it comes back outside the fence, the check-out time is taken
// from when they were last seen on site, and marked as estimated.
export const LONG_GAP_MS = 10 * 60 * 1000;

// How often the app checks location while it is open and the person has a
// fenced job today.
export const FENCE_POLL_MS = 20 * 1000;

// ---------------------------------------------------------------------------

const deg2rad = (deg) => (deg * Math.PI) / 180;

export const metersBetween = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const metersToFeet = (m) => Math.round(Number(m || 0) * 3.28084);

const num = (v) => (v === null || v === undefined || v === "" ? NaN : Number(v));

// The job's fence, normalized, or null when there is no usable fence.
// Accepts a job document or a bare fence object.
export const getJobFence = (jobOrFence) => {
  const f = jobOrFence?.jobFence !== undefined ? jobOrFence.jobFence : jobOrFence;
  if (!f || typeof f !== "object") return null;
  const latitude = num(f.latitude ?? f.lat);
  const longitude = num(f.longitude ?? f.lng);
  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
  const r = num(f.radiusMeters);
  return {
    ...f,
    latitude,
    longitude,
    radiusMeters: Number.isNaN(r) || r <= 0 ? DEFAULT_FENCE_RADIUS_M : r,
    confirmed: f.confirmed === true,
    autoCheckIn: f.autoCheckIn !== false,
  };
};

// Confirmed fence with auto check-in turned on.
export const isFenceActive = (job) => {
  const f = getJobFence(job);
  return !!f && f.confirmed && f.autoCheckIn;
};

// Where a reading sits relative to a fence.
//   inside   counts as on site (used for entering)
//   outside  clearly gone, past the fence plus EXIT_MARGIN_M (used for leaving)
// Between the two is a dead band where nothing changes.
export const fenceStatus = (point, fenceLike) => {
  const fence = getJobFence(fenceLike);
  if (!point || !fence) return null;
  const lat = num(point.latitude);
  const lng = num(point.longitude);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

  const distanceMeters = metersBetween(lat, lng, fence.latitude, fence.longitude);
  const accuracy = num(point.accuracy);
  const buffer = Number.isNaN(accuracy) ? 0 : Math.min(Math.max(accuracy, 0), MAX_ACCURACY_BUFFER_M);
  const effective = distanceMeters - buffer;

  return {
    distanceMeters: Math.round(distanceMeters),
    radiusMeters: fence.radiusMeters,
    bufferMeters: Math.round(buffer),
    inside: effective <= fence.radiusMeters,
    outside: effective > fence.radiusMeters + EXIT_MARGIN_M,
    // How far past the edge, for "you are 40 m outside the fence" messages.
    outsideByMeters: Math.max(0, Math.round(distanceMeters - fence.radiusMeters)),
  };
};

// Fence snapshot saved on a visit at check-in, so check-out uses the same
// fence even if someone edits the job later in the day.
export const fenceSnapshot = (job) => {
  const f = getJobFence(job);
  if (!f || !f.confirmed) return null;
  return { latitude: f.latitude, longitude: f.longitude, radiusMeters: f.radiusMeters };
};

// ---------------------------------------------------------------------------
// The automatic check-in / check-out decision
// ---------------------------------------------------------------------------
//
// Called with every location reading. Returns what to do and the updated
// memory (`state`) to pass in next time. Never touches Firestore itself.
//
// input:
//   point       {latitude, longitude, accuracy}
//   now         millis
//   jobs        today's jobs for this person that have an ACTIVE fence. Each
//               needs: key (jobId_occurrence), job, fence
//   openVisit   the visit they are checked in to right now, or null.
//               Uses: id, jobId, occurrenceDate, fence, checkInInsideFence,
//               lastSeenInsideAt (millis or Date)
//   pastVisits  today's checked-out visits: {jobKey, checkOutAtMs}
//   state       memory from the previous call ({} the first time)
//
// output: { action, job?, visit?, at?, estimated?, reason?, state, inside? }
//   action "check_in"   job        -> check them in automatically
//   action "check_out"  visit, at  -> check them out; `at` is the time to use
//   action "none"                  -> nothing to do
//   inside (open visit)            -> true when the reading was on site

const toMs = (v) => {
  if (!v) return null;
  if (typeof v === "number") return v;
  if (v instanceof Date) return v.getTime();
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.toDate === "function") return v.toDate().getTime();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  const n = new Date(v).getTime();
  return Number.isNaN(n) ? null : n;
};

export const emptyFenceState = () => ({
  insideStreak: {}, // jobKey -> readings in a row inside
  outsideSince: {}, // visitId -> millis first seen clearly outside
  seenInside: {}, // visitId -> true once seen inside the fence
  lastInsideAt: {}, // visitId -> millis last seen inside
  lastOutsideAt: {}, // jobKey -> millis last seen clearly outside
  lastReadingAt: null,
});

export const decideFenceAction = ({
  point,
  now = Date.now(),
  jobs = [],
  openVisit = null,
  openVisitJobKey = null,
  pastVisits = [],
  state: prevState,
}) => {
  const state = {
    ...emptyFenceState(),
    ...(prevState || {}),
  };
  // Copy nested maps so the caller's object is never mutated.
  state.insideStreak = { ...state.insideStreak };
  state.outsideSince = { ...state.outsideSince };
  state.seenInside = { ...state.seenInside };
  state.lastInsideAt = { ...state.lastInsideAt };
  state.lastOutsideAt = { ...state.lastOutsideAt };

  const none = (extra = {}) => ({ action: "none", state, ...extra });

  if (!point) return none({ reason: "no_reading" });
  const accuracy = num(point.accuracy);
  if (!Number.isNaN(accuracy) && accuracy > MAX_USABLE_ACCURACY_M) {
    return none({ reason: "poor_accuracy" });
  }

  const previousReadingAt = state.lastReadingAt;
  state.lastReadingAt = now;

  // Remember when each job was last seen clearly outside. Used to stop an
  // instant re-check-in right after someone checks out while still on site.
  jobs.forEach(({ key, fence }) => {
    const s = fenceStatus(point, fence);
    if (s?.outside) state.lastOutsideAt[key] = now;
  });

  // ---- Checked in somewhere: only ever think about leaving ---------------
  if (openVisit) {
    const fence = getJobFence(openVisit.fence) ? openVisit.fence : null;
    if (!fence) {
      // Checked in by hand to a job with no fence. Leave it alone.
      return none({ reason: "open_visit_no_fence" });
    }

    const vid = openVisit.id;
    if (openVisit.checkInInsideFence === true) state.seenInside[vid] = true;

    const s = fenceStatus(point, fence);
    if (!s) return none({ reason: "no_status" });

    if (s.inside) {
      state.seenInside[vid] = true;
      state.lastInsideAt[vid] = now;
      delete state.outsideSince[vid];
      if (openVisitJobKey) state.insideStreak[openVisitJobKey] = 0;
      return none({ inside: true, status: s });
    }

    if (!s.outside) {
      // Dead band near the edge: no change either way.
      delete state.outsideSince[vid];
      return none({ reason: "edge", status: s });
    }

    // Clearly outside.
    if (!state.seenInside[vid]) {
      // They checked in by hand from outside the fence (an override) and
      // have not been seen inside since. Do not undo their choice.
      return none({ reason: "override_never_inside", status: s });
    }

    const lastInside = Math.max(
      state.lastInsideAt[vid] || 0,
      toMs(openVisit.lastSeenInsideAt) || 0
    ) || null;

    // Came back from a long silence (app was closed) already outside: they
    // left some time ago. Use when they were last seen on site. Right after
    // the app opens there is no previous reading in memory, so fall back to
    // the last on-site time saved on the visit.
    const lastHeard = Math.max(previousReadingAt || 0, lastInside || 0);
    const silentFor = lastHeard ? now - lastHeard : 0;
    if (silentFor > LONG_GAP_MS) {
      return {
        action: "check_out",
        visit: openVisit,
        at: lastInside || now,
        estimated: !!lastInside,
        reason: "left_fence_while_app_closed",
        state: clearVisit(state, vid),
        status: s,
      };
    }

    if (!state.outsideSince[vid]) state.outsideSince[vid] = now;
    const outFor = now - state.outsideSince[vid];
    if (outFor < EXIT_DWELL_MS) {
      return none({ reason: "waiting_to_confirm_exit", status: s });
    }

    // Left and stayed gone. The check-out time is when they were first seen
    // outside, unless there was a long silence before that.
    const firstOutside = state.outsideSince[vid];
    const gapBefore = lastInside ? firstOutside - lastInside : 0;
    const useLastInside = lastInside && gapBefore > LONG_GAP_MS;
    return {
      action: "check_out",
      visit: openVisit,
      at: useLastInside ? lastInside : firstOutside,
      estimated: !!useLastInside,
      reason: "left_fence",
      state: clearVisit(state, vid),
      status: s,
    };
  }

  // ---- Not checked in: look for a fence they are standing in --------------
  let best = null;
  jobs.forEach((entry) => {
    const s = fenceStatus(point, entry.fence);
    if (!s || !s.inside) {
      state.insideStreak[entry.key] = 0;
      return;
    }
    if (!best || s.distanceMeters < best.status.distanceMeters) {
      best = { ...entry, status: s };
    }
  });

  // Only the closest fence builds a streak, so two overlapping jobs at one
  // property cannot both fire.
  Object.keys(state.insideStreak).forEach((k) => {
    if (!best || k !== best.key) state.insideStreak[k] = 0;
  });

  if (!best) return none({ reason: "not_inside_any_fence" });

  // Checked out of this job while still on site? Wait until they have been
  // seen outside before checking them back in automatically.
  const lastCheckOut = pastVisits
    .filter((v) => v.jobKey === best.key)
    .reduce((max, v) => Math.max(max, v.checkOutAtMs || 0), 0);
  if (lastCheckOut && !(state.lastOutsideAt[best.key] > lastCheckOut)) {
    state.insideStreak[best.key] = 0;
    return none({ reason: "checked_out_here_recently", status: best.status });
  }

  state.insideStreak[best.key] = (state.insideStreak[best.key] || 0) + 1;
  if (state.insideStreak[best.key] < ENTER_CONFIRM_READINGS) {
    return none({ reason: "waiting_to_confirm_entry", status: best.status });
  }

  state.insideStreak[best.key] = 0;
  return {
    action: "check_in",
    job: best.job,
    jobKey: best.key,
    reason: "entered_fence",
    state,
    status: best.status,
  };
};

const clearVisit = (state, vid) => {
  delete state.outsideSince[vid];
  delete state.seenInside[vid];
  delete state.lastInsideAt[vid];
  return state;
};
